<script setup lang="ts">
import { VDateInput } from 'vuetify/labs/VDateInput'
import type { MDBSemi } from '@/types/ferme/semi'
import type { MDBVegcoBucket } from '@/types/vegco/bucket'
import type { DataTableHeaders } from '@/plugins/vuetify'
import { notifyError, roundTo, toDate_wd_dd_MMMM, emptyMongoId, getSemiSuperficie, getSemiDateRécoltePrévue } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'

const storeU = useUtilisateurStore()
const dialog = ref(false)
const modifications = ref(false)
const isLoading = ref(true)

const semiSource = ref({} as MDBSemi)
const semi = ref({} as MDBSemi)
const rendementPrévu = ref(0)
const nbBoîtes = computed(() => roundTo(getSemiSuperficie(semi.value) * rendementPrévu.value, 0))
const unitParBoîte = computed(() => unitésParBoîte(semi.value.culture))
const rendementEstChangé = computed(() => semi.value.rendementPrévu != rendementPrévu.value * unitParBoîte.value)
const isValidDateVérification = computed(() => {
  const date = new Date(semi.value.dateVérification)
  return date.getFullYear() > 2000
})
const isValidDateRécoltePrévueFarmit = computed(() => {
  const date = new Date(semi.value.dateDu)
  const dateR = new Date(semi.value.dateRécoltePrévue)
  return dateR.getFullYear() == date.getFullYear() && dateR.getTime() >= date.getTime()
})
const isValidDateRécoltePrévueDJFarmit = computed(() => {
  if (semi.value.degréJour == null)
    return false
  const date = new Date(semi.value.dateDu)
  const dateR = new Date(semi.value.degréJour.dateRécoltePrévueInitiale)
  return dateR.getFullYear() == date.getFullYear() && dateR.getTime() >= date.getTime()
})
const buckets = ref([] as MDBVegcoBucket[])
const headersMaster: DataTableHeaders = [
  { title: 'Date récolte', key: 'formattedDate', width: '176px'},
  //{ title: 'Date publié', key: 'formattedDatePublication', width: '160px' },
  { title: 'Localisation', key: 'localisation', width: '120px'},
  { title: 'Quantité', key: 'formattedQté', width: '120px'},
  { title: 'Priorité', key: 'priorité', width: '120px'},
  //{ title: 'Commande Id', key: 'commandeVenteId', width: '120px'},
  { title: 'Balises', key: 'balisesList', width: '200px' },
  { title: 'Commentaires', key: 'commentaires'},
  { title: 'Actions', key: 'actions', sortable: false, width: '80px' }
];
const masterBuckets = computed(() => {
  return buckets.value.filter(b => b.bucketParentId == emptyMongoId())
});
const formattedMasterBuckets = computed(() => {
  return buckets.value
    .filter(b => b.bucketParentId == emptyMongoId())
    .map(b => ({
      ...b,
      formattedQté: getMasterBucketRealQtéFormatted(b),
      formattedDate: toDate_wd_dd_MMMM(b.date) + (b.dateChangéeManuellement ? ' (*)' : ''),
      //formattedDatePublication: formatDatePublication(b.date, b.datePublication),
      balisesList: b.balises.join(', '),
    }));
});
const headersSlave: DataTableHeaders = [
  { title: 'Date récolte', key: 'formattedDate', width: '160px'},
  { title: 'Localisation', key: 'localisation', width: '120px'},
  { title: 'Quantité', key: 'formattedQté', width: '120px'},
  { title: 'Priorité', key: 'priorité', width: '120px'},
  { title: 'Date publié', key: 'formattedDatePublication', width: '160px' },
  { title: 'Commande Id', key: 'commandeVenteId', width: '120px'},
  { title: 'Commentaires', key: 'commentaires'},
  { title: 'Actions', key: 'actions', sortable: false, width: '80px' }
];

function formattedSlaveBuckets(master: MDBVegcoBucket) {
  return buckets.value
    .filter(b => b.bucketParentId == master.id)
    .map(b => ({
      ...b,
      formattedQté: roundTo(b.quantité / unitParBoîte.value, 0),
      formattedDate: toDate_wd_dd_MMMM(b.date),
      formattedDatePublication: formatDatePublication(b.date, b.datePublication),
      //balisesList: b.balises.join(', '),
    }));
}

let resolve: (value: boolean) => void
watch(dialog, (v) => {
  if (!v) {
    resolve(modifications.value)
  }
})

function getMasterBucketRealQtéFormatted(bucket: MDBVegcoBucket) {
  let f = ''
  if (bucket.semiFermé) {
    f = '0'
  }
  else {
    let qté = bucket.quantité
    qté += buckets.value.filter(b => b.bucketParentId == bucket.id)
      .reduce((acc, b) => acc + b.quantité, 0)

    if (qté != bucket.quantité)
      f = roundTo(qté / unitParBoîte.value, 0).toString() + ' (' + roundTo(bucket.quantité / unitParBoîte.value, 0) + ')'
    else
      f = roundTo(qté / unitParBoîte.value, 0).toString()
  }
  if (bucket.quantitéChangéeManuellement) {
    f += ' (*)'
  }
  return f
}

function formatDatePublication(date: string, datePublication: string) {
  const d = new Date(date)
  const dp = new Date(datePublication)
  return dp.getFullYear() != d.getFullYear() ? 'Non publié' : toDate_wd_dd_MMMM(datePublication)
}

async function openBucketsAsync(semiSrc: MDBSemi, openBucketId: string | null | undefined) {
  isLoading.value = true
  modifications.value = false
  buckets.value = []
  semiSource.value = semiSrc
  semi.value = { ... semiSrc }
  rendementPrévu.value = semi.value.rendementPrévu / unitParBoîte.value
  editSuperficie.value = semi.value.superficieManuelle > 0
  editRécolteManuelle.value = false
  if (new Date(semi.value.dateRécoltePrévueManuelle).getFullYear() > 2000) {
    editRécolteManuelle.value = true
  }

  dialog.value = true
  try {
    await getBuckets()

    if (openBucketId) {
      const bucket = buckets.value.find(b => b.id == openBucketId)
      if (bucket) {
        if (bucket.bucketParentId == emptyMongoId() &&
          buckets.value.find(b => b.bucketParentId == emptyMongoId() && b.id != bucket.id)) {
          // Si on ouvre un bucket splittés, on ouvre le split
          await openSplit()
        }
        else {
          // Sinon on ouvre le bucket
          await editBucket(bucket)
        }
      }
    }
  }
  catch (e) {
    Notify.error(e)
    dialog.value = false
  }
  finally {
    isLoading.value = false
  }

  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

async function getBuckets() {
  const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
  const result = await apiProphet.get(`vgco/sites/${siteCS}/semis/${semi.value.id}/buckets`)
  buckets.value = result.data as MDBVegcoBucket[]
  isLoading.value = false
}

async function confirm() {
  await updateDateRécolteManuelle()
  await updateRendement()
  await updateSuperficie()
  dialog.value = false
}

const editRécolteManuelle = ref(false)
const dateRécolteManuelleEstChangé = computed(() => {
  return semi.value.dateRécoltePrévueManuelle != semiSource.value.dateRécoltePrévueManuelle
})
async function onDateRécoltePencilClick() {
  editRécolteManuelle.value = !editRécolteManuelle.value
  const date = new Date(semi.value.dateRécoltePrévueManuelle)
  if (date.getFullYear() <= 2000) {
    semi.value.dateRécoltePrévueManuelle = getSemiDateRécoltePrévue(semi.value)
  }
  if (!editRécolteManuelle.value)
  {
    semi.value.dateRécoltePrévueManuelle = '2000-01-01'
    if (new Date(semiSource.value.dateRécoltePrévueManuelle).getFullYear() > 2000)
      await updateDateRécolteManuelle()
  }
}
async function updateDateRécolteManuelle() {
  try
  {
    if (!dateRécolteManuelleEstChangé.value)
      return

    isLoading.value = true
    modifications.value = true

    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    const config = { params: { dateRecolte: semi.value.dateRécoltePrévueManuelle } }
    await apiProphet.put(`sites/${siteCS}/semis/${semi.value.id}/daterecolteprevuemanuelle`, null, config)
    semiSource.value.dateRécoltePrévueManuelle = semi.value.dateRécoltePrévueManuelle

    for (const bucket of buckets.value) {
      if (bucket.dateChangéeManuellement && bucket.commandeVenteId == emptyMongoId()) {
        Notify.info('Certains buckets on une date de récolte manuelle, leur date n\'est pas modifiée');
        break
      }
    }

    Notify.success('Modification de la date récolte manuelle avec succès');

    await getBuckets()
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

const editSuperficie = ref(false)
const superficieAuto = computed(() => roundTo(semi.value.superficie, 3))
const superficieManuelleEstChangé = computed(() => {
  return semi.value.superficieManuelle != semiSource.value.superficieManuelle
})
const superficieEstChangé = computed(() => {
  let newSuperficie = semi.value.superficieManuelle
  if (!editSuperficie.value)
    newSuperficie = 0
  return newSuperficie != semiSource.value.superficieManuelle
})
async function onSuperficiePencilClick() {
  editSuperficie.value = !editSuperficie.value
  if (semi.value.superficieManuelle == 0) {
    semi.value.superficieManuelle = semi.value.superficie
  }
  if (!editSuperficie.value) {
    if (semiSource.value.superficieManuelle > 0)
      await updateSuperficie()
  }
}
async function updateSuperficie() {
  try
  {
    if (!superficieEstChangé.value)
      return

    isLoading.value = true
    modifications.value = true

    let newSuperficie = semi.value.superficieManuelle
    if (!editSuperficie.value)
      newSuperficie = 0

    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    const config = { params: { superficie: newSuperficie } }
    await apiProphet.put(`sites/${siteCS}/semis/${semi.value.id}/superficie`, null, config)

    for (const bucket of buckets.value) {
      if (bucket.quantitéChangéeManuellement && bucket.commandeVenteId == emptyMongoId()) {
        Notify.info('Certains buckets on une quantité manuelle, leur quantité n\'est pas modifiée');
        break
      }
    }

    Notify.success('Modification de la superficie avec succès');
    semi.value.superficieManuelle = newSuperficie
    semiSource.value.superficieManuelle = newSuperficie
    await getBuckets()
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

async function updateRendement() {
  try
  {
    const rendement = rendementPrévu.value * unitParBoîte.value
    if (rendement == semi.value.rendementPrévu)
      return

    isLoading.value = true
    modifications.value = true
    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    const config = { params: { rendement: rendement } }
    await apiProphet.put(`sites/${siteCS}/semis/${semi.value.id}/rendement`, null, config)

    for (const bucket of buckets.value) {
      if (bucket.quantitéChangéeManuellement && bucket.commandeVenteId == emptyMongoId()) {
        Notify.info('Certains buckets on une quantité manuelle, leur quantité n\'est pas modifiée');
        break
      }
    }

    Notify.success('Modification du rendement avec succès');
    semi.value.rendementPrévu = rendement
    await getBuckets()
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

const dialogBucket = useTemplateRef('dialogBucket')
async function editBucket(bucket: MDBVegcoBucket) {
  const modifs = await dialogBucket.value?.openBucketEdit(bucket, undefined)
  if (modifs) {
    isLoading.value = true
    modifications.value = true
    // Check si on change la date de vérification

    try {
      const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
      const result = await apiProphet.get(`sites/${siteCS}/semis/${semi.value.id}`)
      semi.value.dateRécoltePrévueManuelle = result.data.dateRécoltePrévueManuelle
      semiSource.value.dateRécoltePrévueManuelle = semi.value.dateRécoltePrévueManuelle
    }
    catch (e) {
      notifyError(e)
    }

    getBuckets()
  }
}

const dialogHistorique = useTemplateRef('dialogHistorique')
function voirHistorique(bucket: MDBVegcoBucket) {
  dialogHistorique.value?.openHistBucketAsync(bucket)
}

const dialogBucketSplit = useTemplateRef('dialogBucketSplit')
async function openSplit() {
  const modifs = await dialogBucketSplit.value?.openSplit(buckets.value)
  if (modifs) {
    isLoading.value = true
    modifications.value = true
    // Check si on change la date de vérification
    getBuckets()
  }
}

async function vérifierMaintenant() {
  try
  {
    isLoading.value = true
    modifications.value = true
    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    await apiProphet.put(`sites/${siteCS}/semis/${semi.value.id}/verifier`)
    Notify.success('Vérification avec succès');
    semi.value.dateVérification = new Date().toISOString()
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

async function changeFermé() {
  try
  {
    isLoading.value = true
    modifications.value = true

    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    const config = { params: { ferme: !semi.value.fermé } }
    await apiProphet.put(`sites/${siteCS}/semis/${semi.value.id}/ferme`, null, config)
    Notify.success('Modification de la fermeture avec succès');
    semi.value.fermé = !semi.value.fermé
    await getBuckets()
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

defineExpose({ openBucketsAsync })
</script>

<template>
  <v-dialog v-model="dialog" max-width="90%" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5 text-medium-emphasis ps-2">Buckets</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="dialog = false"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="semi.numéro" label="Numéro" readonly density="compact" variant="outlined"/>
            <v-text-field v-model="semi.culture" label="Culture" readonly density="compact" variant="outlined"/>
            <v-text-field v-model="semi.variété" label="Variété" readonly density="compact" variant="outlined"/>
            <v-text-field v-model="semi.champ" label="Champ" readonly density="compact" variant="outlined"/>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-if="!editRécolteManuelle" label="Date récolte manuelle"
              model-value="Aucune date" readonly density="compact" variant="outlined"
              append-icon="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" @click:append="onDateRécoltePencilClick" />
            <v-date-input
              v-else v-model="semi.dateRécoltePrévueManuelle" label="Date récolte manuelle" prepend-icon=""
              :append-inner-icon="dateRécolteManuelleEstChangé ? 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' : undefined" append-icon="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M9,8H11V17H9V8M13,8H15V17H13V8Z"
              @click:append="onDateRécoltePencilClick" @click:append-inner="updateDateRécolteManuelle"/>

            <v-date-input
              v-if="isValidDateRécoltePrévueFarmit" v-model="semi.dateRécoltePrévue" label="Date récolte prévue Farmit"
              display-format="normalDateWithWeekday" readonly density="compact" variant="outlined" prepend-icon=""/>
            <v-text-field
              v-else label="Date récolte prévue Farmit" model-value="Aucune date"
              readonly density="compact" variant="outlined" prepend-icon=""/>

            <v-date-input
              v-if="semi.degréJour && isValidDateRécoltePrévueDJFarmit" v-model="semi.degréJour.dateRécoltePrévueInitiale" label="Date récolte prévue DJ"
              display-format="normalDateWithWeekday" readonly density="compact" variant="outlined" prepend-icon=""/>
            <v-text-field
              v-else-if="semi.degréJour != null" label="Date récolte prévue DJ" model-value="Aucune date"
              readonly density="compact" variant="outlined" prepend-icon=""/>

            <v-date-input
              v-if="masterBuckets.length > 0" v-model="masterBuckets[0].date" label="Date bucket 1"
              display-format="normalDateWithWeekday" readonly density="compact" variant="outlined" prepend-icon=""/>
            <v-date-input
              v-if="masterBuckets.length > 1" v-model="masterBuckets[1].date" label="Date bucket 2"
              display-format="normalDateWithWeekday" readonly density="compact" variant="outlined" prepend-icon=""/>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-if="!editSuperficie" v-model="superficieAuto" label="Superficie" type="number" suffix="ac"
              readonly density="compact" variant="outlined"
              append-icon="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" @click:append="onSuperficiePencilClick" />
            <v-text-field
              v-else v-model="semi.superficieManuelle" label="Superficie" type="number" suffix="ac"
                :append-inner-icon="superficieManuelleEstChangé ? 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' : undefined" append-icon="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M9,8H11V17H9V8M13,8H15V17H13V8Z"
              @click:append="onSuperficiePencilClick" @click:append-inner="updateSuperficie"/>

            <v-text-field v-model="nbBoîtes" label="Nombre de boîtes" type="number" readonly density="compact" variant="outlined"/>
            <v-text-field v-model="rendementPrévu" label="Rendement prévu" type="number" suffix="btes/ac" required/>
            <v-btn v-if="rendementEstChangé" color="secondary" variant="flat" @click="updateRendement">Mettre à jour le rendement</v-btn>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <div>
              <v-date-input
                v-if="isValidDateVérification" v-model="semi.dateVérification" label="Date de vérification"
                display-format="normalDateWithWeekday" readonly density="compact" variant="outlined" prepend-icon=""/>
              <v-btn
                label="Date de vérification" color="secondary" variant="flat" @click="vérifierMaintenant">Vérifier maintenant</v-btn>
            </div>
            <div class="mt-12">
              <v-btn color="secondary" variant="flat" @click="openSplit">Séparation</v-btn>
            </div>
            <div class="mt-4">
              <v-btn v-if="semi.fermé" color="green" variant="flat" @click="changeFermé">Ouvrir le semi</v-btn>
              <v-btn v-else color="red" variant="flat" @click="changeFermé">Fermer le semi</v-btn>
            </div>

          </v-col>

        </v-row>
      </v-card-text>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-data-table
          :headers="headersMaster" :items="formattedMasterBuckets" :loading="isLoading" :items-per-page="-1"
          hide-default-footer show-expand :hide-default-header="$vuetify.display.smAndDown">
          <template v-if="$vuetify.display.smAndDown" #item="props">
            <tr>
              <td class="pt-2 pb-2">
                <v-container>
                  <v-row>
                    <v-col cols="8">
                      Date: {{ props.item.formattedDate }}<br/>
                      Localisation: {{ props.item.localisation }}<br/>
                      Quantité: {{ props.item.formattedQté }}<br/>
                      Priorité: {{ props.item.priorité }}<br/>
                      Balises: {{ props.item.balisesList }}<br/>
                      Commentaires: {{ props.item.commentaires }}<br/>
                    </v-col>
                    <v-col cols="4">
                      <v-icon class="me-2 mb-4" size="40" @click="editBucket(props.item)">
                        M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z
                      </v-icon>
                      <v-icon class="me-2 mb-4" size="40" @click="voirHistorique(props.item)">
                        M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z
                      </v-icon>
                      <br/>
                      <v-btn
                        :append-icon="props.isExpanded(props.internalItem) ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'"
                        :text="props.isExpanded(props.internalItem) ? 'Cacher' : 'Sous-buckets'"
                        class="text-none" color="medium-emphasis" variant="text" border
                        @click="props.toggleExpand(props.internalItem)"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </td>
            </tr>
          </template>
          <template #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }">
            <v-btn
              :append-icon="isExpanded(internalItem) ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'"
              :text="isExpanded(internalItem) ? 'Cacher' : 'Sous-buckets'"
              class="text-none" color="medium-emphasis" size="small" variant="text" border
              @click="toggleExpand(internalItem)"
            />
          </template>
          <template #expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="py-2">
                <v-data-table
                  :headers="headersSlave"
                  :items="formattedSlaveBuckets(item)"
                  :loading="isLoading"
                  :items-per-page="-1"
                  hide-default-footer
                  class="mt-2">
                  <template #item.actions="{ item: innerItem }">
                    <v-icon class="me-2" size="small" @click="editBucket(innerItem)">
                      M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z
                    </v-icon>
                    <v-icon class="me-2" size="small" @click="voirHistorique(innerItem)">
                      M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z
                    </v-icon>
                  </template>
                </v-data-table>
              </td>
            </tr>
          </template>
          <template #item.actions="{ item }">
            <v-icon class="me-2" size="small" @click="editBucket(item)">
              M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z
            </v-icon>
            <v-icon size="small" @click="voirHistorique(item)">
              M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
      <span class="ml-5 text-caption">(*) = Changements manuels</span>

      <v-divider class="mt-2"/>

      <v-card-actions class="my-2 d-flex justify-end mr-2">
        <v-btn color="primary" variant="flat" @click="confirm()">
        {{(!dateRécolteManuelleEstChangé && !rendementEstChangé && !superficieEstChangé) ? 'OK' : 'Modifier le semi'}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <DialogBucket ref="dialogBucket"/>
  <DialogHistorique ref="dialogHistorique"/>
  <DialogBucketSplit ref="dialogBucketSplit"/>
</template>
